// Persistent anonymous visitor identity.
//
// A UUID is generated on first visit and stored in BOTH localStorage and a
// long-lived cookie, so the guest is recognised across sessions even if one of
// the two stores is cleared. Pure utility — no React, safe to import anywhere
// (returns null during SSR/prerender).

const KEY = 'fs-guest-id';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readCookie(): string | null {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${KEY}=`));
    return hit ? decodeURIComponent(hit.slice(KEY.length + 1)) : null;
  } catch {
    return null;
  }
}

function writeCookie(id: string): void {
  try {
    document.cookie = `${KEY}=${encodeURIComponent(id)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  // RFC4122-ish fallback for very old browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Return the visitor's stable guest id, creating + persisting one if needed.
 * localStorage is authoritative; the cookie is a backup (and vice versa).
 */
export function getGuestId(): string | null {
  if (typeof window === 'undefined') return null;

  let id: string | null = null;
  try {
    id = localStorage.getItem(KEY);
  } catch {
    /* storage unavailable */
  }
  if (!id || !UUID_RE.test(id)) id = readCookie();
  if (!id || !UUID_RE.test(id)) id = generateId();

  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* ignore */
  }
  writeCookie(id);
  return id;
}
